import { useQuery as useApolloQuery } from '@apollo/client';
import { useEntityFilter } from '@risksmart-app/components/src/contexts/entityFilterContext';
import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { RibbonInput } from '@risksmart-app/trpc/src/routers/frontend/my-items.router';
import type {
  MyRibbonActionsResponseRow,
  MyRibbonAssessmentActivitiesResponseRow,
  MyRibbonAssessmentsResponseRow,
  MyRibbonAttestationRecordsResponseRow,
  MyRibbonChangeRequestsResponseRow,
  MyRibbonControlsResponseRow,
  MyRibbonDocumentsResponseRow,
  MyRibbonIndicatorsResponseRow,
  MyRibbonIssuesResponseRow,
  MyRibbonObligationsResponseRow,
  MyRibbonRisksResponseRow,
} from '@risksmart-app/trpc/src/types';
import {
  GetMyItemsDashboardDocument,
  type GetMyItemsDashboardQuery,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useCallback, useEffect, useMemo } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { useGetQueryVariables } from '../../../pages/dashboards/my-items/hooks/useGetQueryVariables';
import { useDashboardStore } from '../../../pages/dashboards/useDashboardStore';

// Shape Ribbon.tsx consumes — the Apollo `useQuery` returns `{ data }` and
// surfaces errors via an `onError` notification; the tRPC branch mirrors that
// by surfacing the first failing query's error through the same notification.
export interface UseGetMyItemsDashboardResult {
  data: GetMyItemsDashboardQuery | undefined;
  loading: boolean;
}

// The legacy Apollo query variables (built by useGetQueryVariables) — passed in
// by the caller so the flag-off branch keeps the existing where-clause builder.
// Typed against the builder's own return type so the *_Bool_Exp shapes (which
// the generated Exact<{...}> variables type doesn't capture) flow through.
export type MyItemsDashboardGraphqlVariables = ReturnType<
  typeof useGetQueryVariables
>;

const mapOwnerGroups = (
  groups: MyRibbonRisksResponseRow['ownerGroups']
): GetMyItemsDashboardQuery['risk'][number]['ownerGroups'] =>
  groups.map((g) => ({
    UserGroupId: g.UserGroupId,
    group: g.group
      ? { users: g.group.users.map((u) => ({ UserId: u.UserId })) }
      : null,
  }));

const mapContributorGroups = (
  groups: MyRibbonRisksResponseRow['contributorGroups']
): GetMyItemsDashboardQuery['risk'][number]['contributorGroups'] =>
  groups.map((g) => ({
    UserGroupId: g.UserGroupId,
    group: g.group
      ? { users: g.group.users.map((u) => ({ UserId: u.UserId })) }
      : null,
  }));

// The drizzle `parent.ObjectType` is the wider domain `ParentType` union; the
// generated `Parent_Type_Enum` is its value subset (the persisted node types).
// Every value the column can hold is a member of `Parent_Type_Enum`, so we
// resolve it by value lookup rather than a cast. The fallback can't be reached
// for a real CR parent node, but keeps the function total — `ObjectType` only
// feeds `getOwners`'s string comparisons downstream, so the fallback is inert.
const parentTypeEnumValues = Object.values(Parent_Type_Enum);

const toParentTypeEnum = (objectType: string): Parent_Type_Enum =>
  parentTypeEnumValues.find((v) => v === objectType) ?? Parent_Type_Enum.Risk;

const mapChangeRequest = (
  cr: MyRibbonChangeRequestsResponseRow
): GetMyItemsDashboardQuery['change_request'][number] => ({
  ChangeRequestStatus: cr.ChangeRequestStatus,
  CreatedAtTimestamp: cr.CreatedAtTimestamp,
  Id: cr.Id,
  Workflow: cr.Workflow,
  RequestedChanges: cr.RequestedChanges,
  responses: cr.responses.map((r) => ({
    Approved: r.Approved,
    approver: {
      OwnerApprover: r.approver.OwnerApprover,
      level: r.approver.level
        ? {
            Id: r.approver.level.Id,
            ApprovalRuleType: r.approver.level.ApprovalRuleType,
          }
        : null,
      group: r.approver.group
        ? { users: r.approver.group.users.map((u) => ({ UserId: u.UserId })) }
        : null,
      user: r.approver.user ? { Id: r.approver.user.Id } : null,
    },
  })),
  parent: cr.parent
    ? {
        Id: cr.parent.Id,
        SequentialId: cr.parent.SequentialId,
        ObjectType: toParentTypeEnum(cr.parent.ObjectType),
        risk: cr.parent.risk
          ? { Id: cr.parent.risk.Id, Title: cr.parent.risk.Title }
          : null,
        documentFile: cr.parent.documentFile
          ? {
              Version: cr.parent.documentFile.Version,
              parent: cr.parent.documentFile.parent
                ? {
                    Id: cr.parent.documentFile.parent.Id,
                    Title: cr.parent.documentFile.parent.Title,
                  }
                : null,
            }
          : null,
        action: cr.parent.action
          ? { Id: cr.parent.action.Id, Title: cr.parent.action.Title }
          : null,
        issue_assessment: cr.parent.issue_assessment
          ? {
              parent: cr.parent.issue_assessment.parent
                ? {
                    Id: cr.parent.issue_assessment.parent.Id,
                    Title: cr.parent.issue_assessment.parent.Title,
                  }
                : null,
            }
          : null,
        acceptance: cr.parent.acceptance
          ? { Id: cr.parent.acceptance.Id, Title: cr.parent.acceptance.Title }
          : null,
        control: cr.parent.control
          ? { Id: cr.parent.control.Id, Title: cr.parent.control.Title }
          : null,
        issue: cr.parent.issue
          ? { Id: cr.parent.issue.Id, Title: cr.parent.issue.Title }
          : null,
      }
    : null,
  currentUserOwnerList: cr.currentUserOwnerList.map((o) => ({
    UserId: o.UserId,
  })),
});

interface TrpcRibbonData {
  actions: MyRibbonActionsResponseRow[];
  risks: MyRibbonRisksResponseRow[];
  indicators: MyRibbonIndicatorsResponseRow[];
  documents: MyRibbonDocumentsResponseRow[];
  assessments: MyRibbonAssessmentsResponseRow[];
  assessmentActivities: MyRibbonAssessmentActivitiesResponseRow[];
  controls: MyRibbonControlsResponseRow[];
  issues: MyRibbonIssuesResponseRow[];
  obligations: MyRibbonObligationsResponseRow[];
  attestationRecords: MyRibbonAttestationRecordsResponseRow[];
  changeRequests: MyRibbonChangeRequestsResponseRow[];
}

/**
 * Composes the 11 per-entity tRPC ribbon rows into the legacy
 * `GetMyItemsDashboardQuery` shape so the downstream ribbon hooks
 * (useFlattenRibbonData / useRibbonNavigation / useGetMyItemsFilteringTokens)
 * keep working unchanged. Field-by-field, no casts — `__typename` is omitted
 * (optional in the generated type) and nullables are coalesced explicitly.
 */
export const mapTrpcRibbonDataToGraphQL = (
  data: TrpcRibbonData
): GetMyItemsDashboardQuery => ({
  change_request: data.changeRequests.map(mapChangeRequest),
  action: data.actions.map((d) => ({
    Id: d.Id,
    // Legacy `action.Status` is non-null `Action_Status_Enum`; the ribbon read
    // selects the same NOT NULL column.
    Status: d.Status,
    // `DateDue` is non-null in the legacy contract; the ribbon read selects the
    // same NOT NULL column.
    DateDue: d.DateDue,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  risk: data.risks.map((d) => ({
    Id: d.Id,
    Status: d.Status,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  indicator: data.indicators.map((d) => ({
    Id: d.Id,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  document: data.documents.map((d) => ({
    Id: d.Id,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  assessment: data.assessments.map((d) => ({
    Id: d.Id,
    Status: d.Status,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  assessment_activity: data.assessmentActivities.map((d) => ({
    Id: d.Id,
    Status: d.Status,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
  })),
  control: data.controls.map((d) => ({
    Id: d.Id,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  issue: data.issues.map((d) => ({
    Id: d.Id,
    assessment: d.assessment ? { Status: d.assessment.Status } : null,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
  attestation_record: data.attestationRecords.map((d) => ({ Id: d.Id })),
  obligation: data.obligations.map((d) => ({
    Id: d.Id,
    ownerGroups: mapOwnerGroups(d.ownerGroups),
    contributorGroups: mapContributorGroups(d.contributorGroups),
  })),
});

/**
 * Builds the shared `RibbonInput` from the SAME dashboard store state the legacy
 * `getQueryVariables` reads: the 8 ownership flags pass through unmodified and
 * `entityIds` is only attached when non-empty. Ownership is always scoped to the
 * logged-in user server-side.
 */
const useRibbonInput = (): RibbonInput => {
  const { myItemsFilters } = useDashboardStore();
  const { entityIds } = useEntityFilter();

  return useMemo(() => {
    return {
      owner: myItemsFilters.owner,
      contributor: myItemsFilters.contributor,
      groupOwner: myItemsFilters.groupOwner,
      groupContributor: myItemsFilters.groupContributor,
      inheritedOwner: myItemsFilters.inheritedOwner,
      inheritedContributor: myItemsFilters.inheritedContributor,
      inheritedGroupOwner: myItemsFilters.inheritedGroupOwner,
      inheritedGroupContributor: myItemsFilters.inheritedGroupContributor,
      // entityIds only narrows risk/control/issue/action/indicator; omit when
      // empty so the backend treats it as "no entity filter".
      ...(entityIds.length > 0 ? { entityIds } : {}),
    };
  }, [myItemsFilters, entityIds]);
};

/**
 * RSP-6214 — flag-gated my-items ribbon read. With the `trpc` flag ON it fans
 * out the 11 per-entity `frontend.myItems.ribbon*` procedures in parallel and
 * composes them into the legacy `GetMyItemsDashboardQuery` shape; with the flag
 * OFF it runs the legacy `GetMyItemsDashboardDocument` Apollo mega-query
 * (default). Only one branch's queries ever execute — the other is gated via
 * `enabled` / `skip` so the vitest console.warn-is-fatal setup never trips on a
 * stray dual fetch.
 *
 * The caller passes the legacy Apollo `variables` (built by useGetQueryVariables)
 * for the flag-off branch; the tRPC input is derived internally from the SAME
 * dashboard store state.
 */
export const useGetMyItemsDashboard = (
  graphqlVariables: MyItemsDashboardGraphqlVariables
): UseGetMyItemsDashboardResult => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();

  const ribbonInput = useRibbonInput();

  // Stable identity so the error-surfacing effect below can depend on it
  // honestly (no exhaustive-deps suppression). `addNotification` is itself
  // stable from the notifications context.
  const notifyError = useCallback(
    (content: ReactNode) => addNotification({ type: 'error', content }),
    [addNotification]
  );

  // --- tRPC branch: 11 parallel typed queries, only enabled when flag is on ---
  const actions = useQuery({
    ...trpc.frontend.myItems.ribbonActions.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const risks = useQuery({
    ...trpc.frontend.myItems.ribbonRisks.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const indicators = useQuery({
    ...trpc.frontend.myItems.ribbonIndicators.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const documents = useQuery({
    ...trpc.frontend.myItems.ribbonDocuments.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const assessments = useQuery({
    ...trpc.frontend.myItems.ribbonAssessments.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const assessmentActivities = useQuery({
    ...trpc.frontend.myItems.ribbonAssessmentActivities.queryOptions(
      ribbonInput
    ),
    enabled: trpcEnabled,
  });
  const controls = useQuery({
    ...trpc.frontend.myItems.ribbonControls.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const issues = useQuery({
    ...trpc.frontend.myItems.ribbonIssues.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const obligations = useQuery({
    ...trpc.frontend.myItems.ribbonObligations.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const attestationRecords = useQuery({
    ...trpc.frontend.myItems.ribbonAttestationRecords.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });
  const changeRequests = useQuery({
    ...trpc.frontend.myItems.ribbonChangeRequests.queryOptions(ribbonInput),
    enabled: trpcEnabled,
  });

  const trpcQueries = [
    actions,
    risks,
    indicators,
    documents,
    assessments,
    assessmentActivities,
    controls,
    issues,
    obligations,
    attestationRecords,
    changeRequests,
  ];

  const trpcLoading = trpcEnabled && trpcQueries.some((q) => q.isLoading);
  // The first failing query's message — each query's `error` is the typed
  // `TRPCClientErrorLike` (or null); we only need its message string, so read
  // it directly without re-casting the union element type.
  const trpcErrorMessage = trpcEnabled
    ? trpcQueries.find((q) => q.error)?.error?.message
    : undefined;

  // Surface the first failing query's message via the same notification the
  // Apollo branch's onError used.
  useEffect(() => {
    if (trpcErrorMessage) {
      notifyError(trpcErrorMessage);
    }
  }, [trpcErrorMessage, notifyError]);

  const trpcData = useMemo((): GetMyItemsDashboardQuery | undefined => {
    if (!trpcEnabled) {
      return undefined;
    }
    // Wait until every query has produced data — a partial compose would
    // momentarily under-count the ribbon while late queries resolve.
    if (trpcQueries.some((q) => q.data === undefined)) {
      return undefined;
    }

    return mapTrpcRibbonDataToGraphQL({
      actions: actions.data ?? [],
      risks: risks.data ?? [],
      indicators: indicators.data ?? [],
      documents: documents.data ?? [],
      assessments: assessments.data ?? [],
      assessmentActivities: assessmentActivities.data ?? [],
      controls: controls.data ?? [],
      issues: issues.data ?? [],
      obligations: obligations.data ?? [],
      attestationRecords: attestationRecords.data ?? [],
      changeRequests: changeRequests.data ?? [],
    });
    // Depend on each query's `.data` (not the query objects, whose identity
    // changes every render) and `trpcEnabled`. `mapTrpcRibbonDataToGraphQL` is a
    // module-level const (stable) so it's intentionally not a dependency; the
    // `trpcQueries` array is rebuilt each render and reading it for the
    // all-resolved guard is covered by the `.data` deps below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    trpcEnabled,
    actions.data,
    risks.data,
    indicators.data,
    documents.data,
    assessments.data,
    assessmentActivities.data,
    controls.data,
    issues.data,
    obligations.data,
    attestationRecords.data,
    changeRequests.data,
  ]);

  // --- Apollo branch: legacy mega-query, skipped when the flag is on ---
  const { data: graphqlData, loading: graphqlLoading } = useApolloQuery(
    GetMyItemsDashboardDocument,
    {
      variables: graphqlVariables,
      fetchPolicy: 'no-cache',
      skip: trpcEnabled,
      onError: (error) => notifyError(error.message),
    }
  );

  if (trpcEnabled) {
    return { data: trpcData, loading: trpcLoading };
  }

  return { data: graphqlData, loading: graphqlLoading };
};
